import { useMutation, useQuery } from "react-query";
import { fetchWithContext, useFetchContext } from "../fetch";
import { IsAuth0Auth, IsFirebaseAuth, useAuth } from "../AuthProvider";
import { useChangeProtectionGuardContext } from "../../components/ChangeProtectionGuard";
import { useMemo } from "react";
import {
  CertificateDto,
  CloudProvider,
  CloudProviderRegion,
  ClusterCapacityType,
  CompatibilityDto,
  ConductorReleaseDto,
  IObject,
  IProvisioningPermittedValueDto,
  ISignupPermittedValueDto,
  PackageVersionDto,
  Region,
  ReleaseDto,
  WorkflowStatus,
} from "../saastypes";
import { UseQueryOptions, UseQueryResult } from "react-query/types/react/types";
import _isArray from "lodash/isArray";
import _get from "lodash/get";
import {
  FETCH_ALL_RELEASES_PATH,
  FETCH_COMPATIBILITY_LIST,
  SYNC_RELEASES_PATH,
} from "../apiPaths";
import { useAuthMachine } from "auth/state/hook";
import { IdTitle, UserInvite, RefetchFunction } from "./types";
import {
  STALE_TIME_SHORT_SECONDS,
  STALE_TIME_UNLIMITED,
  STALE_TIME_10_MINS,
} from "./constants";

export type CustomQueryOptions = UseQueryOptions<
  any,
  unknown,
  any,
  string[]
> & {
  useLocalMessage: boolean;
};

export const defaultHeaders = (token: string, organizationId?: string) => {
  return {
    Authorization: `Bearer ${token}`,
    ...(organizationId ? { "X-Expected-Organization-Id": organizationId } : {}),
    Accept: `application/json`,
    "Content-Type": `application/json`,
  };
};

export function useAuthHeaders(): object {
  const obj = useAuth();

  if (IsAuth0Auth && obj?.isAuthenticated) {
    const currentUser = obj?.currentUser;
    return defaultHeaders(
      currentUser?.stsTokenManager,
      obj?.currentOrganization?.id
    );
  }

  if (IsFirebaseAuth) {
    const currentState = obj.authService.getSnapshot();
    // Capture the organization rendered by this tab, never fetch the server's latest
    // selection at mutation time. An old tab must fail instead of retargeting its action.
    return defaultHeaders(
      currentState?.context?.currentUser?.accessToken,
      currentState?.context?.currentOrganization?.id
    );
  }

  return {};
}

export function useFetch<T>(
  pathORPathAndCriteria: string | string[],
  reactQueryOptions: any,
  additionalFetchParams: any = {}
): UseQueryResult<T> {
  const fetchContext = useFetchContext();
  const headers: any = useAuthHeaders();
  const fetchParams = { headers: headers };
  const additionalEnabled =
    additionalFetchParams.enabled !== undefined
      ? additionalFetchParams.enabled
      : true;

  // Work arround to allow a criteria attribute
  const [path] = Array.isArray(pathORPathAndCriteria)
    ? pathORPathAndCriteria
    : [pathORPathAndCriteria];

  const resultQueryOptions = {
    enabled:
      additionalEnabled &&
      fetchContext.ready &&
      fetchParams.headers["Authorization"] !== undefined &&
      ("paramsAvailable" in reactQueryOptions
        ? reactQueryOptions.paramsAvailable
        : true),
    keepPreviousData: true,
    retry: (_failureCount: any, error: Response) =>
      ![403, 409].includes(error?.status) && 1,
    ...reactQueryOptions,
  };

  const key = [fetchContext.stack].concat(pathORPathAndCriteria);

  const result = useQuery<T>(
    key,
    () => fetchWithContext(path, fetchContext, fetchParams),
    resultQueryOptions
  );

  return result;
}

export function useAction(
  path: string,
  method = "post",
  callbacks: object,
  isText: boolean
) {
  const fetchContext = useFetchContext();
  const authHeaders = useAuthHeaders();

  return useMutation(
    (mutateParams) =>
      fetchWithContext(
        path,
        fetchContext,
        {
          method,
          headers: authHeaders,
          body: _get(mutateParams, "body"),
        },
        isText
      ),
    callbacks
  );
}

const GUARDED_METHODS = new Set(["post", "put", "patch", "delete"]);

// Decides whether a given action should be gated by the change-protection
// acknowledgement dialog. Guards mutating requests, but NOT: the protection
// toggles themselves, nor the read-only /agent/command calls (GET_*, HEALTH_CHECK)
// that fire on tab/page load.
function shouldGuardAction(
  method?: string,
  path?: string,
  body?: any
): boolean {
  if (!method || !path) return false;
  if (!GUARDED_METHODS.has(String(method).toLowerCase())) return false;
  if (
    path.includes("setClusterChangeProtection") ||
    path.includes("setClusterDeletion") ||
    // The cluster-page toggles are exempt from each other, or change protection could
    // never be turned off. Automatic Failover is the same family of control, and its own
    // confirm-on-enable dialog is the speed bump for its risky direction.
    path.includes("setPairAutomaticFailover")
  ) {
    return false;
  }
  if (path.includes("/agent/command")) {
    try {
      const parsed = typeof body === "string" ? JSON.parse(body || "{}") : body;
      const command = String(parsed?.command ?? "").toUpperCase();
      if (command.startsWith("GET_") || command === "HEALTH_CHECK") {
        return false;
      }
    } catch {
      // Unparseable body: fall through and guard (safer default).
    }
  }
  return true;
}

export function useActionWithPath(
  callbacks: object,
  isText?: boolean,
  headers = {}
) {
  const fetchContext = useFetchContext();
  const authHeaders = useAuthHeaders();
  const changeProtectionGuard = useChangeProtectionGuardContext();
  return useMutation(async (mutateParams) => {
    const actionPath = _get(mutateParams, "path");
    const method = _get(mutateParams, "method");
    const body = _get(mutateParams, "body");
    if (
      changeProtectionGuard.enabled &&
      shouldGuardAction(method, actionPath, body)
    ) {
      const acknowledged = await changeProtectionGuard.requireAck();
      if (!acknowledged) {
        throw new Error(
          "Action cancelled: change protection is enabled for this cluster."
        );
      }
    }
    return fetchWithContext(
      actionPath,
      fetchContext,
      {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          ...headers,
        },
        body,
      },
      isText
    );
  }, callbacks);
}

export function useWorkflowStatus(clusterName: string): {
  data: WorkflowStatus | undefined;
  isFetching: any;
  refetch: RefetchFunction;
} {
  const { data, ...rest } = useFetch<WorkflowStatus>(
    `/getWorkflowStatus?clusterName=${clusterName}`,
    {
      staleTime: STALE_TIME_SHORT_SECONDS,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useAgentCommands(): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(`/agent/commands`, {});
  return {
    data: data,
    ...rest,
  };
}

/** How long to keep waiting for a just-triggered action's row to appear, before calling it an error. */
const NOT_RECORDED_RETRIES = 10;
const NOT_RECORDED_RETRY_MS = 2000;

export function useHistoryEntry(
  clusterName: string,
  logId: string
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
  isNotRecordedYet: boolean;
} {
  const { data, error, ...rest } = useFetch(
    `/agent/workflowsResult/${clusterName}?id=${logId}`,
    {
      // A 404 means the action was accepted but the workflow has not written its row yet, which is the
      // normal state for the first seconds after triggering one — and the page navigates here
      // immediately. Waiting quietly is the correct response; the global QueryCache.onError handler
      // would otherwise raise a failure toast for an action that in fact succeeded.
      retry: (failureCount: number, err: Response) =>
        err?.status === 404
          ? failureCount < NOT_RECORDED_RETRIES
          : err?.status !== 403 && failureCount < 1,
      retryDelay: NOT_RECORDED_RETRY_MS,
      // Report the outcome in the page, which has room to say what is actually happening, rather than
      // as a toast that says only that something went wrong.
      useLocalMessage: true,
    },
    {
      enabled: logId !== undefined && logId !== null && logId.trim().length > 0,
    }
  );
  return {
    data: data,
    error,
    isNotRecordedYet: (error as Response | undefined)?.status === 404,
    ...rest,
  };
}

export function usePackageVersions(resourceName = "conductor"): {
  data: PackageVersionDto[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch<PackageVersionDto[]>(
    `/getPackageVersions?resource=${resourceName}`,
    {}
  );
  return {
    data,
    ...rest,
  };
}

export function useHealthChecks(clusterName: string): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getHealthChecks?clusterName=${clusterName}`,
    {}
  );
  return {
    data: data,
    ...rest,
  };
}

export function useClusterHealth(clusterName: string): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getClusterHealth?clusterName=${clusterName}`,
    {}
  );
  return {
    data: data,
    ...rest,
  };
}

export function useBulkHistoryEntry(
  clusterName: string,
  logIds: Array<string>
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/agent/workflowsResultsBulk/${clusterName}?id=${logIds.join("&id=")}`,
    {},
    {
      enabled: _isArray(logIds) && logIds.length > 0,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useDeploymentInfo(clusterName: string): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getDeploymentsInfo/?clusterName=${clusterName}`,
    {}
  );
  return {
    data: data,
    ...rest,
  };
}

export function useDeploymentInfoFull(clusterName: string): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getDeploymentsInfoFull/?clusterName=${clusterName}`,
    {}
  );
  return {
    data: data,
    ...rest,
  };
}

export function useGetPodsAction(clusterName: string): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/agent/command`,
    {},
    {
      method: "post",
      body: JSON.stringify({
        clusterName: clusterName,
        command: "GET_PODS_NAMES",
        parameters: {},
      }),
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useAllUserInvites(): {
  data: UserInvite[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { data, ...rest } = useFetch<UserInvite[]>("/users/pendingInvites", {
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data: data,
    ...rest,
  };
}

function isValid(str: string | undefined | null) {
  return str !== undefined && str !== null && str.trim().length > 0;
}

export function useCustomerRegions(
  clusterName: string | undefined,
  customerAccountId: string | undefined
): {
  data: IdTitle[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { data, ...rest } = useFetch<string[]>(
    `/getRegions?clusterName=${clusterName}&customerAccountId=${customerAccountId}`,
    {
      paramsAvailable: isValid(clusterName) && isValid(customerAccountId),
      staleTime: STALE_TIME_UNLIMITED,
    }
  );

  // Filter latest versions only
  const regions = useMemo(() => {
    if (data) {
      return data.map((region) => ({ id: region, title: region }));
    }
  }, [data]);

  return {
    data: regions,
    ...rest,
  };
}

export function useCustomerVPCIds(
  clusterName: string | undefined,
  customerAccountId: string | undefined,
  region: string | undefined
): {
  data: IdTitle[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { data, ...rest } = useFetch<string[]>(
    `/getVpcIdsByRegion?clusterName=${clusterName}&customerAccountId=${customerAccountId}&region=${region}`,
    {
      paramsAvailable:
        isValid(clusterName) && isValid(customerAccountId) && isValid(region),
      staleTime: STALE_TIME_UNLIMITED,
    }
  );

  // Filter latest versions only
  const selectItems = useMemo(() => {
    if (data) {
      return data.map((value) => ({ id: value, title: value }));
    }
  }, [data]);

  return {
    data: selectItems,
    ...rest,
  };
}

export function useGcpVpcs(
  clusterName: string | undefined,
  projectId: string | undefined,
  fetchEnabled: boolean = false
): {
  data: any[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const params = new URLSearchParams({
    clusterName: clusterName ?? "",
    projectId: projectId ?? "",
  });
  const { data, ...rest } = useFetch<any[]>(
    `/getGcpVpcsByProject?${params.toString()}`,
    {
      paramsAvailable:
        fetchEnabled && isValid(clusterName) && isValid(projectId),
      staleTime: STALE_TIME_UNLIMITED,
    }
  );

  return {
    data: data,
    ...rest,
  };
}

export function useValidateGcpVpc(
  clusterName: string | undefined,
  projectId: string | undefined,
  vpcName: string | undefined
): {
  data:
    | {
        valid: boolean;
        reason?: string;
        vpcName?: string;
        selfLink?: string;
      }
    | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const params = new URLSearchParams({
    clusterName: clusterName?.toLowerCase() ?? "",
    projectId: projectId ?? "",
    vpcName: vpcName ?? "",
  });
  const { data, ...rest } = useFetch<any>(
    `/validateGcpVpc?${params.toString()}`,
    {
      paramsAvailable:
        isValid(clusterName) && isValid(projectId) && isValid(vpcName),
      staleTime: STALE_TIME_UNLIMITED,
      keepPreviousData: false,
    }
  );

  return {
    data: data,
    ...rest,
  };
}

export function useDomainTags(entityId = ""): {
  data: any[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
} {
  const { data, ...rest } = useFetch<any[]>(
    `/tags?entityType=CLUSTER_URL&entityId=${entityId}`,
    {
      staleTime: STALE_TIME_UNLIMITED,
      refetchOnMount: true,
      keepPreviousData: false,
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useSignupPermittedValues(): {
  data?: ISignupPermittedValueDto;
  isFetching: any;
  refetch: RefetchFunction;
} {
  const { data, ...rest } = useFetch<ISignupPermittedValueDto>(
    "/signupPermittedValues",
    {
      staleTime: STALE_TIME_UNLIMITED,
    }
  );
  return {
    data,
    ...rest,
  };
}

export function useProvisioningPermittedValues(): {
  data: IProvisioningPermittedValueDto | undefined;
  isFetching: any;
  refetch: RefetchFunction;
} {
  const { data, ...rest } = useFetch<IProvisioningPermittedValueDto>(
    "/provisioningPermittedValues",
    {
      staleTime: STALE_TIME_UNLIMITED,
    }
  );
  return {
    data,
    ...rest,
  };
}

export function useCurrentRoles(): {
  data?: string[];
  isFetching: boolean;
  isFetched: boolean;
  refetch: RefetchFunction;
} {
  const { data, ...rest } = useFetch<any[]>(`/currentRoles`, {
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data,
    ...rest,
  };
}
export function useClusterVersionHistory(
  clustername: any,
  resourceName: any
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getClusterVersionsHistory/?clusterName=${clustername}&resource=${resourceName}`,
    {}
  );
  return {
    data: data,
    ...rest,
  };
}
export function useClusterVersionEnvironamentVariables(
  clustername: any,
  versionid: string
): {
  data: any | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  error: any;
} {
  const { data, ...rest } = useFetch(
    `/getClusterVersionEnvVars/?clusterName=${clustername}&versionId=${versionid}`,
    {
      paramsAvailable: isValid(versionid),
    }
  );
  return {
    data: data,
    ...rest,
  };
}

export function useCloudRegion() {
  const { data, ...rest } = useFetch(`/cloudProviderRegions`, {});

  return {
    data: (data as CloudProviderRegion[])?.reduce((result, item) => {
      return { ...result, [item.clusterType]: item.cloudToRegionsMap };
    }, {}) as {
      [key in ClusterCapacityType]: { [key in CloudProvider]: Region[] };
    },
    ...rest,
  };
}

export function useAllReleases() {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<ReleaseDto[]>(FETCH_ALL_RELEASES_PATH, {
    enabled: isLoggedInAndVerified,
    staleTime: STALE_TIME_UNLIMITED,
  });
}

export function useSyncReleases(options?: any) {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<{ ready: boolean; workflowId: string }>(SYNC_RELEASES_PATH, {
    ...options,
    enabled: isLoggedInAndVerified && options?.enabled,
    staleTime: 0,
  });
}

export function useGetAllReleases(workflowId?: string, options?: object) {
  const { data, ...rest } = useFetch<{
    ready: boolean;
    releases?: ReleaseDto[];
    workflowId?: string;
  }>(`${SYNC_RELEASES_PATH}?workflowId=${workflowId}`, {
    ...options,
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data: data,
    ...rest,
  };
}

export function useGetAlConductorReleases() {
  return useFetch<ConductorReleaseDto[]>("/release/fetchConductorReleases", {
    staleTime: STALE_TIME_UNLIMITED,
  });
}

export function useFetchReleaseNotes(version: string, options?: object) {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<{ release_notes: string }>(
    `/release/fetchReleaseNotes?version=${version}`,
    {
      ...options,
      staleTime: 0,
      enabled: isLoggedInAndVerified && !!version,
      retry: 0,
      useLocalMessage: true,
    }
  );
}

export function useFetchDoc(slug: string, options?: object) {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<{ content: string }>(
    `/docs/fetch?slug=${encodeURIComponent(slug)}`,
    {
      ...options,
      staleTime: STALE_TIME_UNLIMITED,
      enabled: isLoggedInAndVerified && !!slug,
      retry: 0,
      useLocalMessage: true,
    }
  );
}

export function useFetchDocImage(path: string, options?: object) {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<{ dataUrl: string }>(
    `/docs/image?path=${encodeURIComponent(path)}`,
    {
      ...options,
      staleTime: STALE_TIME_UNLIMITED,
      enabled: isLoggedInAndVerified && !!path,
      retry: 0,
      useLocalMessage: true,
    }
  );
}

export function useFetchCompatibilityList(option?: IObject) {
  const { authService } = useAuth();
  const [{ isLoggedInAndVerified }] = useAuthMachine(authService);
  return useFetch<CompatibilityDto[]>(FETCH_COMPATIBILITY_LIST, {
    ...option,
    enabled: isLoggedInAndVerified,
    staleTime: STALE_TIME_10_MINS,
  });
}

export function useFetchAllCertificates(clusterName: string, options?: object) {
  return useFetch<CertificateDto[]>(
    `/customCertificates/getAll?clusterName=${clusterName}`,
    {
      ...options,
      staleTime: STALE_TIME_UNLIMITED,
    }
  );
}

export function useFixedDomains(): {
  data: string[] | undefined;
  isFetching: any;
  refetch: RefetchFunction;
  isError: any;
} {
  const { data, ...rest } = useFetch<string[]>(`/getFixedDomains`, {
    staleTime: STALE_TIME_UNLIMITED,
  });
  return {
    data,
    ...rest,
  };
}
